/**
 * The town the map is cut out of.
 *
 * Three attempts got here. The first scattered blocks on a lattice near the map
 * and deliberately left a gap against it, so nothing grew out of a wall you
 * were standing next to — which is exactly backwards, because in a real place
 * the wall beside you *is* the face of a building. The second fixed that by
 * filling the map's negative space, so the pockets the rooms enclose became the
 * buildings they were carved from and the space outside became the town. That
 * was right, and it looked wrong: every pocket was emitted as a stack of boxes,
 * neighbouring boxes shared their side faces, and the whole skyline crawled
 * with z-fighting triangles. It was also a mountain range — the far field grew
 * as it receded, so the map sat at the bottom of a canyon.
 *
 * So the mass is a heightfield now. One height per cell, and a face is emitted
 * only where a cell is taller than its neighbour: no two surfaces ever occupy
 * the same plane, and the count drops with the merge rather than rising with
 * the detail. Storeys band the faces that are close enough to read, the far
 * field falls away instead of climbing, and the tall things in the distance are
 * landmarks the map asked for by name — cooling towers on Nuke, minarets on
 * Mirage, campaniles on Inferno.
 *
 * None of it is collidable or in the navmesh. The compiler still owns every
 * surface you can touch; this is only what you see.
 */

import * as THREE from 'three';
import type { CompiledLevel } from 'levelspec';
import { decomposeToRects, key } from 'levelspec';
import type { Theme } from 'levelspec/generate';
import { buildBlock, type BlockKind, type Piece } from './buildings.ts';

function rng(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h ^= h << 13;
    h >>>= 0;
    h ^= h >> 17;
    h ^= h << 5;
    h >>>= 0;
    return h / 4294967296;
  };
}

/** How far past the map's own extent the town keeps going, in cells. */
const SURROUND = 30;
/** One floor of a building. */
const STOREY = 3.4;
/** Beyond this many metres a facade is too far to read, so it is left plain. */
const DETAIL_RANGE = 55;

interface Column {
  top: number;
  tone: number;
  /** True for the pockets the map's own rooms are carved out of. */
  inner: boolean;
}

/** A plot of the surrounding town that gets a building rather than a slab. */
interface Plot {
  /** Centre, in canonical cells. */
  cx: number;
  cy: number;
  /** Extent in metres. */
  w: number;
  d: number;
  /** Top of the podium the crown stands on. */
  base: number;
  height: number;
  tone: number;
  kind: BlockKind;
}

// ---------------------------------------------------------------------------
// Landmarks
// ---------------------------------------------------------------------------

/**
 * The tall things on the horizon, by name.
 *
 * A skyline is what tells you which town you are in before you have read a
 * single callout, and it is the one part of the surroundings that cannot be
 * derived from the floorplan — no arrangement of the negative space around
 * Nuke will ever produce a cooling tower. So each map names its own, and the
 * shapes are built here.
 */
type Landmark = (rand: () => number, base: number, colour: THREE.Color) => THREE.Object3D;

const lathe = (profile: [number, number][], seg = 20): THREE.BufferGeometry =>
  new THREE.LatheGeometry(profile.map(([r, y]) => new THREE.Vector2(r, y)), seg);

const LANDMARKS: Record<string, Landmark> = {
  /** A hyperboloid cooling tower, plume and all. */
  cooling_tower: (rand, base, colour) => {
    const h = 34 + rand() * 16;
    const throat = 0.62 + rand() * 0.08;
    const r0 = h * 0.30;
    const profile: [number, number][] = [];
    for (let i = 0; i <= 12; i++) {
      const t = i / 12;
      // A hyperbola pinched at four fifths of the height, flaring at the lip.
      const r = r0 * Math.sqrt(1 + Math.pow((t - 0.82) / throat, 2) * 1.5) * (t > 0.94 ? 1.06 : 1);
      profile.push([r, t * h]);
    }
    const g = new THREE.Group();
    const shell = new THREE.Mesh(lathe(profile, 24), mat(colour, 0.95));
    shell.material.side = THREE.DoubleSide;
    g.add(shell);
    const steam = new THREE.Mesh(
      new THREE.SphereGeometry(r0 * 0.8, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.14, depthWrite: false }),
    );
    steam.position.y = h + r0 * 0.5;
    steam.scale.set(1, 0.55, 1);
    g.add(steam);
    g.position.y = base;
    return g;
  },
  /** A reactor containment dome on a drum. */
  dome: (rand, base, colour) => {
    const r = 9 + rand() * 5;
    const drum = 8 + rand() * 6;
    const g = new THREE.Group();
    const d = new THREE.Mesh(new THREE.CylinderGeometry(r, r, drum, 20), mat(colour, 0.9));
    d.position.y = drum / 2;
    g.add(d);
    const cap = new THREE.Mesh(
      new THREE.SphereGeometry(r, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2),
      mat(colour.clone().multiplyScalar(1.08), 0.7),
    );
    cap.position.y = drum;
    g.add(cap);
    const band = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 1.02, r * 1.02, 1.6, 20),
      mat(new THREE.Color(0x8f99a2), 0.8),
    );
    band.position.y = drum * 0.78;
    g.add(band);
    g.position.y = base;
    return g;
  },
  /** A banded flue stack. */
  chimney: (rand, base, colour) => {
    const h = 26 + rand() * 20;
    const r = 1.6 + rand() * 0.9;
    const g = new THREE.Group();
    const bands = Math.max(3, Math.round(h / 7));
    for (let i = 0; i < bands; i++) {
      const y0 = (i / bands) * h;
      const y1 = ((i + 1) / bands) * h;
      const rr = r * (1 - 0.3 * (i / bands));
      const seg = new THREE.Mesh(
        new THREE.CylinderGeometry(rr * 0.94, rr, y1 - y0, 12),
        mat(i % 2 ? new THREE.Color(0x30383f) : colour.clone().multiplyScalar(1.18), 0.9),
      );
      seg.position.y = (y0 + y1) / 2;
      g.add(seg);
    }
    g.position.y = base;
    return g;
  },
  /**
   * A tank on a leg frame.
   *
   * The first version stood it on a single central stalk the same colour as
   * the tank, and against a town of the same colour the stalk vanished: what
   * you saw was a large pale ball hanging in the sky. Four dark legs and a
   * ring beam read as a structure from any distance.
   */
  water_tank: (rand, base, colour) => {
    const legs = 12 + rand() * 8;
    const r = 4 + rand() * 2.5;
    const g = new THREE.Group();
    const steel = mat(colour.clone().multiplyScalar(0.62), 0.85);
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.5, legs, 0.5), steel);
      leg.position.set(sx * r * 0.62, legs / 2, sz * r * 0.62);
      leg.rotation.z = -sx * 0.06;
      leg.rotation.x = sz * 0.06;
      g.add(leg);
    }
    for (const f of [0.4, 0.75] as const) {
      const brace = new THREE.Mesh(new THREE.BoxGeometry(r * 1.5, 0.35, r * 1.5), steel);
      brace.position.y = legs * f;
      g.add(brace);
    }
    const tank = new THREE.Mesh(
      lathe([[0, 0], [r, r * 0.35], [r, r * 1.1], [r * 0.5, r * 1.5], [0, r * 1.6]], 14),
      mat(colour.clone().multiplyScalar(1.12), 0.8),
    );
    tank.position.y = legs;
    g.add(tank);
    g.position.y = base;
    return g;
  },
  /** A tower with a balcony and a cap. */
  minaret: (rand, base, colour) => {
    const h = 20 + rand() * 12;
    const r = 1.5 + rand() * 0.6;
    const g = new THREE.Group();
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.8, r, h, 12), mat(colour, 0.9));
    shaft.position.y = h / 2;
    g.add(shaft);
    const gallery = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.6, r * 1.5, 1.2, 12), mat(colour, 0.85));
    gallery.position.y = h * 0.74;
    g.add(gallery);
    const cap = new THREE.Mesh(new THREE.ConeGeometry(r * 1.05, r * 4, 12), mat(colour.clone().multiplyScalar(0.85), 0.7));
    cap.position.y = h + r * 2;
    g.add(cap);
    g.position.y = base;
    return g;
  },
  /** A square bell tower with a tiled pyramid. */
  campanile: (rand, base, colour) => {
    const h = 17 + rand() * 10;
    const w = 4 + rand() * 1.6;
    const g = new THREE.Group();
    const shaft = new THREE.Mesh(new THREE.BoxGeometry(w, h, w), mat(colour, 0.92));
    shaft.position.y = h / 2;
    g.add(shaft);
    const roof = new THREE.Mesh(new THREE.ConeGeometry(w * 0.85, w * 1.3, 4), mat(new THREE.Color(0xa4aeb7), 0.85));
    roof.position.y = h + w * 0.65;
    roof.rotation.y = Math.PI / 4;
    g.add(roof);
    g.position.y = base;
    return g;
  },
  /** A grain or fuel silo, in a cluster. */
  silo: (rand, base, colour) => {
    const g = new THREE.Group();
    const n = 2 + Math.floor(rand() * 3);
    const r = 3.4 + rand() * 1.6;
    for (let i = 0; i < n; i++) {
      const h = 16 + rand() * 10;
      const body = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 14), mat(colour.clone().multiplyScalar(1.1), 0.9));
      body.position.set(i * r * 2.1, h / 2, 0);
      g.add(body);
      const cap = new THREE.Mesh(new THREE.ConeGeometry(r, r * 0.8, 14), mat(colour, 0.85));
      cap.position.set(i * r * 2.1, h + r * 0.4, 0);
      g.add(cap);
    }
    g.position.y = base;
    return g;
  },
  /** A stepped pyramid, jungle-temple sized. */
  ziggurat: (rand, base, colour) => {
    const g = new THREE.Group();
    const tiers = 4 + Math.floor(rand() * 3);
    let w = 26 + rand() * 12;
    let y = 0;
    for (let i = 0; i < tiers; i++) {
      const h = 3.4 + rand() * 1.6;
      const tier = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, w),
        mat(colour.clone().multiplyScalar(1 - i * 0.045), 0.95),
      );
      tier.position.y = y + h / 2;
      g.add(tier);
      y += h;
      w *= 0.76;
    }
    const shrine = new THREE.Mesh(new THREE.BoxGeometry(w * 1.1, 5, w * 1.1), mat(colour.clone().multiplyScalar(0.88), 0.9));
    shrine.position.y = y + 2.5;
    g.add(shrine);
    g.position.y = base;
    return g;
  },
  /** A tapered stone needle. */
  obelisk: (rand, base, colour) => {
    const h = 18 + rand() * 12;
    const w = 2.2 + rand();
    const g = new THREE.Group();
    const shaft = new THREE.Mesh(lathe([[w, 0], [w * 0.62, h], [0, h]], 4), mat(colour, 0.92));
    shaft.rotation.y = Math.PI / 4;
    g.add(shaft);
    const cap = new THREE.Mesh(new THREE.ConeGeometry(w * 0.62, w * 1.6, 4), mat(colour.clone().multiplyScalar(1.1), 0.8));
    cap.position.y = h + w * 0.8;
    cap.rotation.y = Math.PI / 4;
    g.add(cap);
    g.position.y = base;
    return g;
  },
  /** A church spire on a square tower. */
  spire: (rand, base, colour) => {
    const h = 14 + rand() * 8;
    const w = 3.4 + rand() * 1.2;
    const g = new THREE.Group();
    const tower = new THREE.Mesh(new THREE.BoxGeometry(w, h, w), mat(colour, 0.92));
    tower.position.y = h / 2;
    g.add(tower);
    const cone = new THREE.Mesh(
      new THREE.ConeGeometry(w * 0.72, h * 0.85, 8),
      mat(new THREE.Color(0x767f88), 0.7),
    );
    cone.position.y = h + h * 0.42;
    g.add(cone);
    g.position.y = base;
    return g;
  },
  /** A transmission pylon, all struts and crossarms. */
  pylon: (rand, base, colour) => {
    const h = 22 + rand() * 10;
    const c = mat(colour.clone().multiplyScalar(0.8), 0.85);
    const g = new THREE.Group();
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.55, h, 0.55), c);
      leg.position.set(sx * 2.6, h / 2, sz * 2.6);
      leg.rotation.z = -sx * 0.05;
      leg.rotation.x = sz * 0.05;
      g.add(leg);
    }
    for (const [f, len] of [[0.62, 13], [0.82, 10], [0.97, 7]] as const) {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(len, 0.5, 0.5), c);
      arm.position.y = h * f;
      g.add(arm);
    }
    g.position.y = base;
    return g;
  },
  /** A dockside gantry. */
  crane: (rand, base, colour) => {
    const h = 20 + rand() * 10;
    const arm = 16 + rand() * 10;
    const c = mat(new THREE.Color(0x2b333b), 0.85);
    const g = new THREE.Group();
    for (const dx of [-3, 3]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(1.1, h, 1.1), c);
      leg.position.set(dx, h / 2, 0);
      g.add(leg);
    }
    const boom = new THREE.Mesh(new THREE.BoxGeometry(1.3, 1.3, arm), c);
    boom.position.set(0, h, arm / 2 - 4);
    g.add(boom);
    const house = new THREE.Mesh(new THREE.BoxGeometry(7, 3.4, 5), mat(colour, 0.9));
    house.position.y = h + 2.4;
    g.add(house);
    g.position.y = base;
    return g;
  },
};

function mat(colour: THREE.Color, rough: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: colour, roughness: rough, metalness: 0.04 });
}

// ---------------------------------------------------------------------------

/** How the surrounding town is put together. */
export interface SceneryOptions {
  /** What stands on the horizon. */
  skyline?: string[];
  /** How the surrounding buildings are fenestrated. */
  facade?: 'punched' | 'banded' | 'industrial';
  roofs?: 'flat' | 'pitched';
  /** Which building types the town is made of. */
  blocks?: BlockKind[];
  accent?: number;
  daylight?: boolean;
  /**
   * How much town to build, 0..1. The quality tier's lever: a phone builds a
   * shallower skirt of buildings round the map than a desktop, and the fog
   * closes over the difference.
   */
  detail?: number;
}

export function buildScenery(
  level: CompiledLevel,
  grid: number,
  bounds: THREE.Box3,
  theme: Theme,
  seed: string,
  opts: SceneryOptions = {},
): THREE.Group {
  const {
    skyline = [],
    facade = 'punched',
    roofs = 'flat',
    blocks = [],
    accent: accentHex = 0xd8543a,
    daylight = true,
    detail = 1,
  } = opts;
  const rand = rng(seed);
  const group = new THREE.Group();

  // --- which cells the map floors -----------------------------------------
  const floor = new Set<string>();
  const deckArea = new Map<number, number>();
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const s of level.solids) {
    if (s.role !== 'floor' && s.role !== 'roof') continue;
    // Tally how much floor sits at each height, to find the one people are
    // actually standing on.
    const at = Math.round(s.box.max[2] * 4) / 4;
    const a = (s.box.max[0] - s.box.min[0]) * (s.box.max[1] - s.box.min[1]);
    deckArea.set(at, (deckArea.get(at) ?? 0) + a);
    const x0 = Math.round(s.box.min[0] / grid);
    const x1 = Math.round(s.box.max[0] / grid);
    const y0 = Math.round(s.box.min[1] / grid);
    const y1 = Math.round(s.box.max[1] / grid);
    for (let x = x0; x < x1; x++)
      for (let y = y0; y < y1; y++) {
        floor.add(key(x, y));
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
  }
  if (!floor.size) return group;

  const lo = { x: minX - SURROUND, y: minY - SURROUND };
  const hi = { x: maxX + SURROUND, y: maxY + SURROUND };
  /*
   * Where the town is founded, and where it is measured from.
   *
   * The mass is filled down to the very bottom of the level so nothing floats,
   * but heights are set from the *deck* — the height most of the map's floor
   * is at. On a single-storey map the two are the same. On a two-storey one
   * they are five metres apart, and measuring a skyline from the basement
   * slab put every building on Nuke below its own outside wall.
   */
  const ground = bounds.min.y - 1;
  const deck = [...deckArea].sort((a, b) => b[1] - a[1])[0]?.[0] ?? bounds.min.y;
  const wallTop = bounds.max.y;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const reach = Math.max(maxX - minX, maxY - minY) / 2;

  // --- connected regions of everything the map is not ----------------------
  const seen = new Set<string>();
  const regions: { cells: string[]; enclosed: boolean }[] = [];
  for (let x = lo.x; x <= hi.x; x++) {
    for (let y = lo.y; y <= hi.y; y++) {
      const k = key(x, y);
      if (floor.has(k) || seen.has(k)) continue;
      const cells: string[] = [];
      let enclosed = true;
      const stack = [[x, y] as [number, number]];
      seen.add(k);
      while (stack.length) {
        const [ax, ay] = stack.pop()!;
        cells.push(key(ax, ay));
        // Touching the outer frame means this pocket is the world, not a
        // building — the difference between a courtyard and the town.
        if (ax <= lo.x || ax >= hi.x || ay <= lo.y || ay >= hi.y) enclosed = false;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const nx = ax + dx;
          const ny = ay + dy;
          if (nx < lo.x || nx > hi.x || ny < lo.y || ny > hi.y) continue;
          const nk = key(nx, ny);
          if (floor.has(nk) || seen.has(nk)) continue;
          seen.add(nk);
          stack.push([nx, ny]);
        }
      }
      regions.push({ cells, enclosed });
    }
  }

  // --- one height per cell -------------------------------------------------
  const column = new Map<string, Column>();
  const crowns: Plot[] = [];
  const away = (x: number, y: number): number => Math.hypot(x - cx, y - cy) / Math.max(1, reach);

  for (const region of regions) {
    if (region.enclosed) {
      /*
       * A pocket the rooms surround: the building they were carved out of. One
       * height for the whole pocket, because it is one building — a courtyard
       * that changed height every few metres would read as rubble.
       */
      const top = Math.max(wallTop, deck + 4) - 0.6 + (1 + Math.floor(rand() * 3)) * STOREY + rand() * 1.4;
      const tone = 0.82 + rand() * 0.3;
      for (const c of region.cells) column.set(c, { top, tone, inner: true });
      // A stair housing or tank on the roof, so the roofline is not flat.
      const rects = decomposeToRects(region.cells).sort((a, b) => b[2] * b[3] - a[2] * a[3]);
      const big = rects[0];
      if (big && big[2] >= 3 && big[3] >= 3 && rand() < 0.75) {
        const roofTop = top + 1.6 + rand() * 2.2;
        for (let x = big[0] + 1; x < big[0] + big[2] - 1; x++)
          for (let y = big[1] + 1; y < big[1] + big[3] - 1; y++)
            column.set(key(x, y), { top: roofTop, tone: tone * 0.93, inner: true });
      }
      continue;
    }

    /*
     * Everything outside. The town is divided into plots, every plot that
     * touches the map is built on — that is the whole point, the building
     * starts where the street stops — and the plots fall away as they recede
     * so the map is not standing at the bottom of a shaft. What is tall in the
     * distance is a landmark, deliberately, not another block of flats.
     */
    // A plot is about twelve metres across whatever the map's grid is, so the
    // town has the same block size everywhere rather than a size that depends
    // on how finely the radar happened to be traced.
    const plot = Math.max(4, Math.round(12 / grid));
    const buckets = new Map<string, string[]>();
    for (const c of region.cells) {
      const [x, y] = c.split(',').map(Number);
      const bk = `${Math.floor(x / plot)},${Math.floor(y / plot)}`;
      let arr = buckets.get(bk);
      if (!arr) buckets.set(bk, (arr = []));
      arr.push(c);
    }
    for (const [bk, cells] of buckets) {
      const [bx, by] = bk.split(',').map(Number);
      const d = away(bx * plot + plot / 2, by * plot + plot / 2);
      /*
       * The quality lever.
       *
       * The first ring of buildings is the street you are standing in and is
       * never cut. Beyond it the town is silhouette: a phone builds less of it
       * and stops sooner, and the fog closes over where the rest would have
       * been. Thinned as well as cut short, because a shorter town of the same
       * density still costs most of what the full one did.
       */
      if (d > 0.95 + detail * 2.2) continue;
      if (d > 1.15 && rand() > 0.35 + detail * 0.65) continue;
      // Gaps are streets. Near the map they are rare, because a hole in the
      // first row of buildings is a hole in the wall you are standing against.
      if (d > 1.12 && rand() < 0.16 + Math.min(0.5, (d - 1.12) * 0.5)) continue;
      const near = Math.max(0, 1 - Math.max(0, d - 1) / 1.5);
      const storeys = 1 + Math.floor(rand() * 2.2 + near * 1.3);
      // Quantised to whole storeys, so two plots that came out the same height
      // read as one long building rather than two blocks that nearly line up —
      // and capped against the plot's own width, because a block twice as tall
      // as it is wide stops being a building and becomes a chimney.
      const cap = deck + Math.max(10, plot * grid * 1.6);
      const top = Math.min(cap, deck + Math.max(3.5, (wallTop - deck) * 0.8) + storeys * STOREY);
      const tone = 0.72 + rand() * 0.46;
      for (const c of cells) column.set(c, { top, tone, inner: false });

      /*
       * And on top of the podium, an actual building.
       *
       * The heightfield fills the plot exactly, so the street wall and the map
       * wall still meet — that part has to stay. What it cannot do is have a
       * shape. So the fill becomes a podium of a storey or two and the rest of
       * the building sits on it as a crown: a tower set back, a drum, a
       * sawtooth shed, a pagoda. Only plots that are mostly intact get one; a
       * bucket the map has eaten half of has no site to build on.
       */
      if (!blocks.length) continue;
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
      for (const c of cells) {
        const [cxx, cyy] = c.split(',').map(Number);
        if (cxx < x0) x0 = cxx;
        if (cxx > x1) x1 = cxx;
        if (cyy < y0) y0 = cyy;
        if (cyy > y1) y1 = cyy;
      }
      const cw = x1 - x0 + 1;
      const ch = y1 - y0 + 1;
      if (cells.length < cw * ch * 0.85 || Math.min(cw, ch) < 3) continue;
      if (rand() > 0.62) continue;
      crowns.push({
        cx: (x0 + x1 + 1) / 2,
        cy: (y0 + y1 + 1) / 2,
        w: cw * grid - 0.4,
        d: ch * grid - 0.4,
        base: top,
        // Crown height is set from the plot's own width and from how tall the
        // map itself stands, so a hill town gets houses and a city gets
        // towers, rather than every town getting the same twenty metres.
        height: Math.max(
          4,
          Math.min(plot * grid * 0.95, (wallTop - deck) * 1.5) * (0.55 + rand() * 0.75),
        ),
        tone,
        kind: blocks[Math.floor(rand() * blocks.length)],
      });
    }
  }

  // --- landmarks -----------------------------------------------------------
  if (skyline.length) {
    const spots: [number, number][] = [];
    for (let i = 0; i < 400 && spots.length < skyline.length * 2; i++) {
      const a = rand() * Math.PI * 2;
      const r = reach * (1.25 + rand() * 0.55);
      const x = Math.round(cx + Math.cos(a) * r);
      const y = Math.round(cy + Math.sin(a) * r);
      if (x < lo.x + 2 || x > hi.x - 2 || y < lo.y + 2 || y > hi.y - 2) continue;
      if (floor.has(key(x, y))) continue;
      if (spots.some(([px, py]) => Math.hypot(px - x, py - y) < 9)) continue;
      spots.push([x, y]);
    }
    // The town is background. Cooler and paler than the interior so it sits
    // behind the fog rather than competing with the room you are standing in.
    const base = new THREE.Color(0xbcc4cb);
    for (let i = 0; i < spots.length; i++) {
      const make = LANDMARKS[skyline[i % skyline.length]];
      if (!make) continue;
      const [x, y] = spots[i];
      const obj = make(rand, deck, base.clone().multiplyScalar(0.9 + rand() * 0.35));
      // Canonical (x, y) becomes Three (x, -y).
      obj.position.x = (x + 0.5) * grid;
      obj.position.z = -(y + 0.5) * grid;
      obj.rotation.y = rand() * Math.PI * 2;
      group.add(obj);
      // Clear the plot under it, so a block does not grow through the tower.
      for (let ax = x - 3; ax <= x + 3; ax++)
        for (let ay = y - 3; ay <= y + 3; ay++) column.delete(key(ax, ay));
    }
  }

  // --- geometry ------------------------------------------------------------
  const pos: number[] = [];
  const norm: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const base = new THREE.Color(0xbcc4cb);
  const glass = new THREE.Color(0x39434d);
  const tmp = new THREE.Color();

  /**
   * One quad, wound to face the way its normal says.
   *
   * Which corner order is front-facing depends on the axis, the direction and
   * which way the merge happened to sweep, and reasoning it out per case is how
   * you end up with a skyline you can see through from one side only. So the
   * winding is measured instead: cross the first two edges, and if it disagrees
   * with the normal, reverse the indices.
   */
  const quad = (v: number[], n: number[], c: THREE.Color): void => {
    const start = pos.length / 3;
    for (let k = 0; k < 4; k++) {
      pos.push(v[k * 3], v[k * 3 + 1], v[k * 3 + 2]);
      norm.push(n[0], n[1], n[2]);
      col.push(c.r, c.g, c.b);
    }
    const e1 = [v[3] - v[0], v[4] - v[1], v[5] - v[2]];
    const e2 = [v[6] - v[0], v[7] - v[1], v[8] - v[2]];
    const cross = [
      e1[1] * e2[2] - e1[2] * e2[1],
      e1[2] * e2[0] - e1[0] * e2[2],
      e1[0] * e2[1] - e1[1] * e2[0],
    ];
    const facing = cross[0] * n[0] + cross[1] * n[1] + cross[2] * n[2] >= 0;
    if (facing) idx.push(start, start + 1, start + 2, start, start + 2, start + 3);
    else idx.push(start, start + 2, start + 1, start, start + 3, start + 2);
  };

  const topAt = (x: number, y: number): number => column.get(key(x, y))?.top ?? -Infinity;

  /*
   * Roofs: one rectangle of equal height at a time, so no two are coplanar.
   *
   * A flat roof is a single quad. A pitched one is two slopes meeting over the
   * rectangle's long axis with a gable at each end, and it is what turns a
   * Mediterranean hill town from a stack of boxes into a town — the tiled
   * roofline is most of what you actually recognise about Inferno. Only
   * rectangles wide enough to carry a ridge get one; the leftovers stay flat,
   * which is what happens on a real roofscape anyway.
   */
  const tile = new THREE.Color(0xa4aeb7);
  const byTop = new Map<number, string[]>();
  for (const [k, c] of column) {
    const q = Math.round(c.top * 20) / 20;
    let arr = byTop.get(q);
    if (!arr) byTop.set(q, (arr = []));
    arr.push(k);
  }
  for (const [top, cells] of byTop) {
    for (const [rx, ry, rw, rh] of decomposeToRects(cells)) {
      const c = column.get(key(rx, ry))!;
      const x0 = rx * grid;
      const x1 = (rx + rw) * grid;
      const z0 = -(ry + rh) * grid;
      const z1 = -ry * grid;
      const pitch = roofs === 'pitched' && Math.min(rw, rh) >= 2 && Math.max(rw, rh) <= 12 && rand() < 0.7;
      if (!pitch) {
        tmp.copy(base).multiplyScalar(c.tone * 0.88);
        quad([x0, top, z1, x1, top, z1, x1, top, z0, x0, top, z0], [0, 1, 0], tmp);
        continue;
      }
      const ridgeAlongX = rw >= rh;
      const span = (ridgeAlongX ? rh : rw) * grid;
      const rise = Math.min(2.4, span * 0.32);
      const eave = 0.35;
      const near = tile.clone().multiplyScalar(c.tone * 0.98);
      const far = tile.clone().multiplyScalar(c.tone * 0.82);
      if (ridgeAlongX) {
        const zm = (z0 + z1) / 2;
        quad([x0 - eave, top, z1 + eave, x1 + eave, top, z1 + eave, x1, top + rise, zm, x0, top + rise, zm], [0, 0.6, 0.8], near);
        quad([x0 - eave, top, z0 - eave, x1 + eave, top, z0 - eave, x1, top + rise, zm, x0, top + rise, zm], [0, 0.6, -0.8], far);
        // Gable ends, as quads with a doubled apex.
        tmp.copy(base).multiplyScalar(c.tone);
        quad([x0, top, z0, x0, top, z1, x0, top + rise, zm, x0, top + rise, zm], [-1, 0, 0], tmp);
        quad([x1, top, z0, x1, top, z1, x1, top + rise, zm, x1, top + rise, zm], [1, 0, 0], tmp);
      } else {
        const xm = (x0 + x1) / 2;
        quad([x0 - eave, top, z0 - eave, x0 - eave, top, z1 + eave, xm, top + rise, z1, xm, top + rise, z0], [-0.8, 0.6, 0], far);
        quad([x1 + eave, top, z0 - eave, x1 + eave, top, z1 + eave, xm, top + rise, z1, xm, top + rise, z0], [0.8, 0.6, 0], near);
        tmp.copy(base).multiplyScalar(c.tone);
        quad([x0, top, z0, x1, top, z0, xm, top + rise, z0, xm, top + rise, z0], [0, 0, -1], tmp);
        quad([x0, top, z1, x1, top, z1, xm, top + rise, z1, xm, top + rise, z1], [0, 0, 1], tmp);
      }
    }
  }

  /**
   * A wall face, storey by storey.
   *
   * A four-storey slab and a four-storey building are the same box; what makes
   * one of them a building is that you can count the floors and see where the
   * windows are. So a face close enough to read gets its storeys, and how those
   * storeys are lit depends on what kind of town this is — a run of punched
   * openings in an old wall reads nothing like a continuous glazing band, and
   * putting the office idiom on Dust II was most of why the sand looked wrong.
   * A face too far to tell is left as the single quad it may as well be.
   */
  const face = (
    ax: number, ay: number, bx: number, by: number,
    zLo: number, zHi: number, n: [number, number, number], c: Column, dist: number,
  ): void => {
    tmp.copy(base).multiplyScalar(c.tone);
    if (dist > DETAIL_RANGE || zHi - zLo < STOREY * 0.9) {
      quad([ax, zLo, ay, bx, zLo, by, bx, zHi, by, ax, zHi, ay], n, tmp);
      return;
    }
    const wall = tmp.clone();
    const spandrel = tmp.clone().multiplyScalar(facade === 'industrial' ? 0.9 : 0.82);
    const win = glass.clone().lerp(wall, 0.35).multiplyScalar(facade === 'banded' ? 0.55 : 0.3);
    const span = Math.hypot(bx - ax, by - ay);
    const ux = (bx - ax) / (span || 1);
    const uy = (by - ay) / (span || 1);
    /** A stripe of the run, as a fraction along it, at one height band. */
    const strip = (t0: number, t1: number, z0: number, z1: number, colour: THREE.Color): void => {
      if (z1 - z0 < 0.02 || t1 - t0 < 0.02) return;
      const p0x = ax + ux * t0, p0y = ay + uy * t0;
      const p1x = ax + ux * t1, p1y = ay + uy * t1;
      quad([p0x, z0, p0y, p1x, z0, p1y, p1x, z1, p1y, p0x, z1, p0y], n, colour);
    };

    let z = zHi;
    let storey = 0;
    while (z > zLo + 0.01) {
      const sill = Math.max(zLo, z - STOREY * 0.46);
      const head = Math.max(zLo, z - STOREY * 0.24);
      const floorLine = Math.max(zLo, z - STOREY);
      // The band above the openings, always full width.
      strip(0, span, head, z, wall);
      /*
       * Wall between the openings, never behind them.
       *
       * This used to lay a full-width band of wall across the window height
       * and then paint the panes on top of it — two quads on the same plane,
       * with the same normal, over the same rectangle. That is z-fighting by
       * construction, and on a distant tower with forty storeys of it the
       * whole elevation crawls as the camera moves. It was reported from
       * looking at the game, which is the wrong way to find this.
       *
       * So a run of wall at window height is emitted as pier, opening, pier,
       * opening, pier — each piece owning its own stretch of the run and no
       * two of them overlapping anywhere.
       */
      if (facade === 'banded') {
        strip(0, span, sill, head, win);
      } else if (facade === 'punched') {
        // Openings on a bay of about three metres, offset every other storey
        // so the elevation is not a grid of identical holes.
        const bay = 3.0;
        const bays = Math.max(1, Math.round(span / bay));
        const w = span / bays;
        const shift = storey % 2 ? w * 0.5 : 0;
        let cut = 0;
        for (let i = 0; i < bays; i++) {
          const a0 = i * w + shift + w * 0.28;
          const a1 = i * w + shift + w * 0.72;
          if (a1 > span) break;
          strip(cut, a0, sill, head, wall);
          strip(a0, a1, sill, head, win);
          cut = a1;
        }
        strip(cut, span, sill, head, wall);
      } else {
        // Industrial: ribbed cladding and the odd louvre, no glazing at all.
        const cladding = storey % 2 ? wall : tmp.clone().multiplyScalar(0.93);
        if (span > 4 && storey % 3 === 1) {
          const l0 = span * 0.42;
          const l1 = span * 0.58;
          const lz = sill + (head - sill) * 0.2;
          strip(0, l0, sill, head, cladding);
          strip(l1, span, sill, head, cladding);
          strip(l0, l1, sill, lz, cladding);
          strip(l0, l1, lz, head, win);
        } else {
          strip(0, span, sill, head, cladding);
        }
      }
      strip(0, span, floorLine, sill, spandrel);
      z = floorLine;
      storey++;
    }
    // A lip at the parapet, so the roof edge catches the light.
    quad(
      [ax, zHi, ay, bx, zHi, by, bx, zHi + 0.35, by, ax, zHi + 0.35, ay],
      n,
      tmp.clone().multiplyScalar(1.14),
    );
  };

  /*
   * Sides, merged along each direction.
   *
   * A face exists only where a cell stands taller than the neighbour it faces,
   * and it runs from the neighbour's height to its own — so the shared parts of
   * two touching buildings are never drawn twice, which is what was making the
   * skyline shimmer.
   */
  const eye = new THREE.Vector3((cx + 0.5) * grid, 0, -(cy + 0.5) * grid);
  const distTo = (x: number, y: number): number =>
    Math.hypot((x + 0.5) * grid - eye.x, -(y + 0.5) * grid - eye.z) - reach * grid;

  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    // Sweep perpendicular to the face so equal runs can be merged.
    const alongX = dy !== 0;
    for (let a = alongX ? lo.y : lo.x; a <= (alongX ? hi.y : hi.x); a++) {
      let run: { b0: number; zLo: number; zHi: number; c: Column } | null = null;
      const flush = (b1: number): void => {
        if (!run) return;
        const { b0, zLo, zHi, c } = run;
        run = null;
        if (zHi - zLo < 0.02) return;
        // The plane this face lies in, in canonical cells.
        const px = alongX ? b0 : a + (dx > 0 ? 1 : 0);
        const py = alongX ? a + (dy > 0 ? 1 : 0) : b0;
        let v: [number, number, number, number];
        let n: [number, number, number];
        if (alongX) {
          const zc = -py * grid;
          v = [px * grid, zc, b1 * grid, zc];
          n = [0, 0, dy > 0 ? -1 : 1];
        } else {
          const xc = px * grid;
          v = [xc, -b0 * grid, xc, -b1 * grid];
          n = [dx > 0 ? 1 : -1, 0, 0];
        }
        const [ax, ay, bx, by] = v;
        face(ax, ay, bx, by, zLo, zHi, n, c, distTo(alongX ? (b0 + b1) / 2 : a, alongX ? a : (b0 + b1) / 2));
      };
      for (let b = alongX ? lo.x : lo.y; b <= (alongX ? hi.x : hi.y); b++) {
        const x = alongX ? b : a;
        const y = alongX ? a : b;
        const c = column.get(key(x, y));
        const mine = c?.top ?? -Infinity;
        const theirs = topAt(x + dx, y + dy);
        const zHi = mine;
        const zLo = Math.max(ground - 1, theirs === -Infinity ? ground - 1 : theirs);
        const solid = c !== undefined && zHi > zLo + 0.02;
        if (
          run &&
          solid &&
          Math.abs(run.zHi - zHi) < 0.02 &&
          Math.abs(run.zLo - zLo) < 0.02 &&
          Math.abs(run.c.tone - c!.tone) < 0.001
        )
          continue;
        flush(b);
        if (solid) run = { b0: b, zLo, zHi, c: c! };
      }
      flush(alongX ? hi.x + 1 : hi.y + 1);
    }
  }

  /*
   * The crowns, baked into the same buffers.
   *
   * Each piece arrives as geometry in its plot's local frame; the transform is
   * applied here rather than by adding a hundred meshes to the scene, so the
   * whole town is still one or two draw calls however many buildings it has.
   * The lit pieces — signage, glazing bands, the neon on a Tokyo drum — go
   * into a second buffer that does not take the scene's light, because a sign
   * that goes dark at night is not a sign.
   */
  const ePos: number[] = [];
  const eNorm: number[] = [];
  const eCol: number[] = [];
  const eIdx: number[] = [];
  const accent = new THREE.Color(accentHex);
  const trim = new THREE.Color(0xc7ced4);
  const m4 = new THREE.Matrix4();
  const n3 = new THREE.Matrix3();

  const bake = (piece: Piece, at: THREE.Matrix4): void => {
    const g = piece.geo.index ? piece.geo.toNonIndexed() : piece.geo;
    const p = g.getAttribute('position');
    const nrm = g.getAttribute('normal');
    // Signage glows at night and is simply glazing by day. A whitewashed hill
    // town with luminous stripes down it is a lighting bug, not a look.
    const lit = piece.emissive === true && !daylight;
    const toP = lit ? ePos : pos;
    const toN = lit ? eNorm : norm;
    const toC = lit ? eCol : col;
    const toI = lit ? eIdx : idx;
    const start = toP.length / 3;
    n3.setFromMatrix4(at);
    const v = new THREE.Vector3();
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i).applyMatrix4(at);
      toP.push(v.x, v.y, v.z);
      if (nrm) {
        v.fromBufferAttribute(nrm, i).applyNormalMatrix(n3).normalize();
        toN.push(v.x, v.y, v.z);
      } else toN.push(0, 1, 0);
      toC.push(piece.colour.r, piece.colour.g, piece.colour.b);
    }
    for (let i = 0; i < p.count; i++) toI.push(start + i);
    if (g !== piece.geo) g.dispose();
    piece.geo.dispose();
  };

  /*
   * Skywalks.
   *
   * Two towers with a bridge between them is one of the cheapest shapes in
   * architecture and one of the most legible: it says city, it says the two
   * buildings are one complex, and it puts something in the gap that the eye
   * would otherwise read as empty. They are drawn between crowns that are
   * close enough and roughly level, at a storey they share.
   */
  if (skyline.includes('crane') || blocks.includes('tower') || blocks.includes('glassbox')) {
    const linked = new Set<number>();
    for (let i = 0; i < crowns.length; i++) {
      if (linked.has(i)) continue;
      for (let j = i + 1; j < crowns.length; j++) {
        if (linked.has(j)) continue;
        const a = crowns[i];
        const b = crowns[j];
        const dx = (b.cx - a.cx) * grid;
        const dy = (b.cy - a.cy) * grid;
        const gap = Math.hypot(dx, dy);
        // Along an axis, far enough apart to be a bridge and near enough to be
        // one span, and standing at heights a deck could join.
        if (gap < 10 || gap > 34) continue;
        if (Math.abs(dx) > 3 && Math.abs(dy) > 3) continue;
        if (Math.abs(a.base + a.height - (b.base + b.height)) > 6) continue;
        if (rand() > 0.4) continue;
        linked.add(i);
        linked.add(j);
        const y = Math.min(a.base + a.height, b.base + b.height) * 0.62 + Math.max(a.base, b.base) * 0.38;
        const w = 2.6 + rand() * 1.6;
        const ax = a.cx * grid;
        const az = -a.cy * grid;
        const bx = b.cx * grid;
        const bz = -b.cy * grid;
        const alongX = Math.abs(dx) >= Math.abs(dy);
        const x0 = alongX ? Math.min(ax, bx) : ax - w / 2;
        const x1 = alongX ? Math.max(ax, bx) : ax + w / 2;
        const z0 = alongX ? az - w / 2 : Math.min(az, bz);
        const z1 = alongX ? az + w / 2 : Math.max(az, bz);
        const deck = base.clone().multiplyScalar(a.tone * 1.05);
        // Deck, soffit and two glazed sides.
        quad([x0, y, z0, x1, y, z0, x1, y, z1, x0, y, z1], [0, 1, 0], deck);
        quad([x0, y - 2.4, z1, x1, y - 2.4, z1, x1, y - 2.4, z0, x0, y - 2.4, z0], [0, -1, 0], deck.clone().multiplyScalar(0.8));
        const side = glass.clone().lerp(deck, 0.35);
        if (alongX) {
          quad([x0, y - 2.4, z0, x1, y - 2.4, z0, x1, y, z0, x0, y, z0], [0, 0, -1], side);
          quad([x0, y - 2.4, z1, x1, y - 2.4, z1, x1, y, z1, x0, y, z1], [0, 0, 1], side);
        } else {
          quad([x0, y - 2.4, z0, x0, y - 2.4, z1, x0, y, z1, x0, y, z0], [-1, 0, 0], side);
          quad([x1, y - 2.4, z0, x1, y - 2.4, z1, x1, y, z1, x1, y, z0], [1, 0, 0], side);
        }
        break;
      }
    }
  }

  for (const c of crowns) {
    const wall = base.clone().multiplyScalar(c.tone);
    const pieces = buildBlock(c.kind, {
      w: c.w,
      d: c.d,
      height: c.height,
      base: 0,
      wall,
      trim: trim.clone().multiplyScalar(c.tone * 1.05),
      accent: accent.clone(),
      glass: glass.clone(),
      rand,
    });
    // Canonical (x, y) becomes Three (x, -y).
    m4.makeTranslation(c.cx * grid, c.base, -c.cy * grid);
    for (const piece of pieces) bake(piece, m4);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(norm, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  geo.computeBoundingSphere();

  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: theme.surface?.roughness ?? 0.94,
      metalness: 0,
    }),
  );
  mesh.name = 'scenery';
  group.add(mesh);

  if (ePos.length) {
    const lit = new THREE.BufferGeometry();
    lit.setAttribute('position', new THREE.Float32BufferAttribute(ePos, 3));
    lit.setAttribute('normal', new THREE.Float32BufferAttribute(eNorm, 3));
    lit.setAttribute('color', new THREE.Float32BufferAttribute(eCol, 3));
    lit.setIndex(eIdx);
    lit.computeBoundingSphere();
    /*
     * Unlit, on purpose.
     *
     * A standard material takes its emissive colour from a uniform, and vertex
     * colours modulate the diffuse term — so lighting these as emissive gave
     * every sign in the city the same white glow with a faintly coloured
     * surface under it. A basic material shows the vertex colour at full
     * strength and ignores the scene's lights entirely, which is what a lit
     * sign at night actually does.
     */
    const signs = new THREE.Mesh(
      lit,
      new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
    );
    signs.name = 'scenery-signs';
    group.add(signs);
  }
  return group;
}
